import React from 'react'
import CodeMirrorMerge from 'react-codemirror-merge'
import {css} from '@emotion/react'
import {useTranslations} from 'next-intl'

import {useAppCtx} from '../../../contexts/appCtx'
import TwoPanelLayout from '../../../components/layouts/TwoPanelLayout'
import Button from '../../../components/elements/Button'
import BasicDialog from '../../../components/elements/BasicDialog'
import useCodeMirrorExtensions from '../../../components/elements/TextArea/useCodeMirrorExtensions'
import TextEditor from '../../../components/modules/TextEditor'


const Original = CodeMirrorMerge.Original
const Modified = CodeMirrorMerge.Modified


export default function CodeComparerLayout({
                                             dataFormat,
                                             format,

                                             leftInputLabel,
                                             leftInputDescription,
                                             rightPanelLabel,
                                             rightPanelDescription,
                                           }) {
  const styles = {
    bottomControl: css`
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-end;

      height: 36px;
    `,
  }

  const t = useTranslations()

  const [leftInput, setLeftInput] = React.useState('')
  const onLeftInputChange = (textInput) => {
    setLeftInput(textInput)
  }

  const [rightInput, setRightInput] = React.useState('')
  const onRightInputChange = (outputResult) => {
    setRightInput(outputResult)
  }

  const appCtx = useAppCtx()

  const [dialogOpen, setDialogOpen] = React.useState(false)
  const onOpenDialog = React.useCallback(() => {
    setDialogOpen(true)
  }, [])

  const onCloseDialog = React.useCallback(() => {
    setDialogOpen(false)
  }, [])

  return (
    <>
      <TwoPanelLayout
        leftPanelName={leftInputLabel ?? t('text-compare-layout.left-input-label')}
        leftPanelTop={leftInputDescription ?? t('text-compare-layout.left-input-description')}
        leftPanelContent={
          <TextEditor
            id="left-input"
            lang={dataFormat}
            value={leftInput}
            onChange={onLeftInputChange}
            autoFocus={true}
          />
        }
        leftPanelBottom={
          <div css={styles.bottomControl}>
            {appCtx.isMobile && (
              <Button
                css={styles.button}
                disabled={!leftInput || !rightInput}
                onClick={onOpenDialog}
              >{t('text-compare-layout.compare-button')}</Button>
            )}
          </div>
        }

        rightPanelName={rightPanelLabel ?? t('text-compare-layout.right-input-label')}
        rightPanelTop={rightPanelDescription ?? t(`text-compare-layout.right-input-description`)}
        rightPanelContent={
          <TextEditor
            id="right-input"
            lang={dataFormat}
            value={rightInput}
            onChange={onRightInputChange}
          />
        }
        rightPanelBottom={
          <div css={styles.bottomControl}>
            <Button
              css={styles.button}
              disabled={!leftInput || !rightInput}
              onClick={onOpenDialog}
            >{t('text-compare-layout.compare-button')}</Button>
          </div>
        }
      />

      <CompareDialog
        open={dialogOpen}
        onClose={onCloseDialog}
        leftInput={leftInput}
        rightInput={rightInput}
        format={format}
      />
    </>
  )
}

function CompareDialog({open, onClose, format, dataFormat, leftInput, rightInput}) {
  const styles = {
    mergeViewer: css`
      position: relative;
    `,
  }
  const t = useTranslations()

  const leftResult = useFormattedResult(leftInput, format)
  const rightResult = useFormattedResult(rightInput, format)

  const extensions = useCodeMirrorExtensions(dataFormat)

  return (
    <BasicDialog
      css={css`
        .MuiDialog-paper {
          min-width: 60%;
        }
      `}
      title={t('text-compare-layout.compare-dialog-title')}
      open={open}
      onClose={onClose}
      autoFullScreen={true}
      maxWidth={false}
    >
      <CodeMirrorMerge css={styles.mergeViewer}>
        <Original
          readOnly={true}
          extensions={extensions}
          value={leftResult}
        />
        <Modified
          readOnly={true}
          extensions={extensions}
          value={rightResult}
        />
      </CodeMirrorMerge>
    </BasicDialog>
  )
}

function useFormattedResult(input, format) {
  const [result, setResult] = React.useState('')
  React.useEffect(() => {
    if (input) {
      let isSubscribed = true

      Promise.resolve()
        .then(() => format(input))
        .then(rightResult => {
          if (isSubscribed) {
            setResult(rightResult)
          }
        })
        .catch(e => {
          if (isSubscribed) {
            setResult(e.toString())
          }
        })
      return () => isSubscribed = false
    } else {
      setResult('')
    }
  }, [input])

  return result
}
